import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// List of realistic first names
const firstNames = [
  'James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
  'William', 'Elizabeth', 'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Charles', 'Karen', 'Emma', 'Olivia', 'Ava', 'Isabella',
  'Sophia', 'Charlotte', 'Mia', 'Amelia', 'Harper', 'Evelyn'
];

// List of realistic last names
const lastNames = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson',
  'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White'
];

// List of email domains
const emailDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com'];

// Generate a random date between start and end
const randomDate = (start: Date, end: Date) => {
  return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
};

// Generate a random name
const generateName = () => {
  const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
  const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
  return `${firstName} ${lastName}`;
};

// Generate a random email based on name
const generateEmail = (name: string) => {
  const cleanName = name.toLowerCase().replace(' ', '.');
  const domain = emailDomains[Math.floor(Math.random() * emailDomains.length)];
  const randomNum = Math.floor(Math.random() * 1000);
  return `${cleanName}${randomNum}@${domain}`;
};

// Generate check-in time with 70% probability
const generateCheckInTime = (purchaseDate: Date, eventStartDate: Date) => {
  const shouldCheckIn = Math.random() < 0.7;
  if (!shouldCheckIn) return null;
  
  // Check-in time should be after purchase and near event start
  const checkInStart = new Date(Math.max(purchaseDate.getTime(), eventStartDate.getTime() - (60 * 60 * 1000))); // 1 hour before event
  const checkInEnd = new Date(eventStartDate.getTime() + (2 * 60 * 60 * 1000)); // 2 hours after event start
  return randomDate(checkInStart, checkInEnd);
};

interface TicketType {
  id: string;
  name: string;
  description?: string;
  price: number;
  capacity: number;
  ticketsSold?: number;
  isVisible?: boolean;
  saleStartDate?: Date;
  saleEndDate?: Date;
}

interface GenerateAttendeesOptions {
  count: number;
  eventId: string;
  startDate: Date;
  endDate: Date;
  ticketTypes: TicketType[];
}

export const generateTestAttendees = async ({
   count,
   eventId,
   startDate,
   endDate,
   ticketTypes,
}: GenerateAttendeesOptions) => {
   try {
      // Get current event data
      const eventRef = doc(db, 'events', eventId);
      const eventSnapshot = await getDoc(eventRef);
      if (!eventSnapshot.exists()) {
         throw new Error('Event not found');
      }

      const eventData = eventSnapshot.data();

      const currentAttendees = eventData.attendees || [];
      let totalRevenue = eventData.revenue || 0;

      // Generate new attendees
      const newAttendees = [];
      const ticketSalesCount = new Map<string, number>();

      // Initialize ticket sales count
      ticketTypes.forEach((ticket) => {
         ticketSalesCount.set(ticket.id, ticket.ticketsSold || 0);
      });

      for (let i = 0; i < count; i++) {
         // Find available ticket type
         const availableTickets = ticketTypes.filter(
            (ticket) => (ticketSalesCount.get(ticket.id) || 0) < ticket.capacity
         );

         if (availableTickets.length === 0) {
            break;
         }

         // Randomly select a ticket type
         const selectedTicket =
            availableTickets[Math.floor(Math.random() * availableTickets.length)];

         // Generate attendee data
         const name = generateName();
         const purchaseDate = randomDate(
            new Date(startDate.getTime() - 30 * 24 * 60 * 60 * 1000),
            startDate
         );
         const checkInTime = generateCheckInTime(purchaseDate, startDate);

         const attendee = {
            id: `test-${Date.now()}-${i}`,
            name,
            email: generateEmail(name),
            ticketType: selectedTicket.id,
            ticketTypeName: selectedTicket.name,
            purchaseDate,
            checkInTime,
            price: selectedTicket.price,
         };

         newAttendees.push(attendee);

         // Update ticket sales count and revenue
         ticketSalesCount.set(
            selectedTicket.id,
            (ticketSalesCount.get(selectedTicket.id) || 0) + 1
         );
         totalRevenue += selectedTicket.price;

         if (i > 0 && i % 10 === 0) {
         }
      }

      // Update event with new attendees and stats
      const updatedTicketTypes = ticketTypes.map((ticket) => ({
         ...ticket,
         ticketsSold: ticketSalesCount.get(ticket.id) || ticket.ticketsSold || 0,
      }));

      await updateDoc(eventRef, {
         attendees: [...currentAttendees, ...newAttendees],
         ticketTypes: updatedTicketTypes,
         revenue: totalRevenue,
         ticketsSold: currentAttendees.length + newAttendees.length,
         lastUpdated: new Date(),
      });

      return newAttendees;
   } catch (error) {
      console.error('Error in generateTestAttendees:', error);
      throw error;
   }
};

// Helper function to run from browser console
(window as any).generateEventAttendees = async (eventId: string, count: number = 50) => {
   try {
      // Fetch event data first
      const eventRef = doc(db, 'events', eventId);
      const eventSnapshot = await getDoc(eventRef);
      if (!eventSnapshot.exists()) {
         throw new Error('Event not found');
      }

      const eventData = eventSnapshot.data();
      const startDate = eventData.startDate.toDate();
      const endDate = eventData.endDate.toDate();

      if (!eventData.ticketTypes || eventData.ticketTypes.length === 0) {
         throw new Error('Event has no ticket types defined');
      }

      await generateTestAttendees({
         count,
         eventId,
         startDate,
         endDate,
         ticketTypes: eventData.ticketTypes,
      });
   } catch (error) {
      console.error('❌ Error:', error);
   }
}; 